// The workspace "recent views" entry — one row in the "⋯" menu in UXView's header.
//
// Backed by the `workspace_recent_views` collection (see api-service
// `app/domain/conversations/recent_view_dao.py`). Both sides derive the SAME identity from
// (cardType, kind, contextRef, viewParams), so a view the agent opened and the identical view the
// user re-picked from the menu collapse to one entry instead of stacking duplicates.
//
// The server stores a sha1 of that identity as `entry_key`; the client uses the un-hashed
// canonical string as its local key (same inputs, same normalisation — hashing would only add an
// async SubtleCrypto round-trip for no benefit, since these keys never leave the browser).
import type { SamModule } from '@/components/SAM/samActionTypes';

/** Where an entry came from. Mirrors the SOURCE_* constants in `recent_view_dao.py`. */
export type RecentViewSource = 'agent' | 'user_filter' | 'user_create' | 'user_open' | 'replay';

/** What a recent-views entry points at when it isn't a plain filtered list — a bound record
 *  (`property`/`engagement`/`communication`/…) or one of the singleton workflow surfaces. */
export interface RecentContextRef {
  type: string;
  id: string;
}

export interface RecentEntry {
  /** Deterministic identity — see `recentViewKey`. Stable across sessions and reloads. */
  key: string;
  /** The workspace module / surface the entry re-opens. */
  cardType: SamModule;
  /** The action type that produced it (e.g. `OPEN_TASKS_PANEL`) — part of the identity, because
   *  two different surfaces can share a module and an empty filter. */
  kind: string;
  /** Heading shown in the menu. Re-validated from the server row on every load. */
  title: string;
  /** The one-line caption under the heading (the agent's narration, or a UI-authored summary). */
  subtitle?: string;
  contextRef?: RecentContextRef;
  /** The filter to re-apply on replay — handed straight back to the module's REST list endpoint. */
  viewParams: Record<string, unknown>;
  source: RecentViewSource;
  /** The turn whose reply produced this view, for "Tap to open chat". */
  messageId?: string;
  /** Epoch ms of the last time this view was opened — the menu's sort key. */
  lastAccessedAt: number;
}

/** Filter keys that describe pagination, not the view. Kept in sync with `_VOLATILE_PARAMS`. */
const VOLATILE_PARAMS = new Set(['skip', 'limit', 'offset', 'page']);

/** Recursively sort an arbitrary JSON value's object keys, matching Python's `json.dumps(...,
 *  sort_keys=True)` — which sorts at EVERY nesting level, not just the top one. Only the top-level
 *  call (`normalizeViewParams`) also strips pagination/empty keys; nested objects just get their
 *  keys ordered so `JSON.stringify` agrees with the server's serialization for the same value. */
function sortKeysDeep(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeysDeep);
  if (v !== null && typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(obj).sort()) out[k] = sortKeysDeep(obj[k]);
    return out;
  }
  return v;
}

/** Drop pagination + empty values and sort the keys (recursively — see `sortKeysDeep`), so
 *  `{status:'open'}` and `{status:'open', skip:0, assignee:''}` describe the same view, and any
 *  nested-object filter value hashes identically to the server's `json.dumps(sort_keys=True)`. */
export function normalizeViewParams(params?: Record<string, unknown> | null): Record<string, unknown> {
  if (!params) return {};
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(params).sort()) {
    const v = params[k];
    if (VOLATILE_PARAMS.has(k)) continue;
    if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) continue;
    out[k] = sortKeysDeep(v);
  }
  return out;
}

/** The entry's identity: (surface, bound record, filter). Must stay in lockstep with `_entry_key`
 *  in `recent_view_dao.py`, which hashes the same three values in the same order.
 *
 *  Deliberately NOT part of the identity: title/caption (the agent words them differently every
 *  turn — the old menu deduped on title and both over- and under-merged as a result) and the
 *  action `kind` (contextRef already separates the surfaces that share a module, and the manual
 *  filter path has no action type to supply). */
export function recentViewKey(input: {
  cardType: string;
  contextRef?: RecentContextRef | null;
  viewParams?: Record<string, unknown> | null;
}): string {
  const ctx = input.contextRef ? `${input.contextRef.type || ''}:${input.contextRef.id || ''}` : '';
  return JSON.stringify([input.cardType || '', ctx, normalizeViewParams(input.viewParams)]);
}

/** Upsert `entry` into a newest-first list: an existing entry with the same key is replaced and
 *  moved to the front, so re-picking a view from the menu makes it the most recent one. */
export function upsertRecent(list: RecentEntry[], entry: RecentEntry): RecentEntry[] {
  const rest = list.filter((e) => e.key !== entry.key);
  return [entry, ...rest];
}

/** Fixed `contextRef.type` values every consumer here matches by literal string. Mirrors
 *  `ContextRefType` in api-service's `actions.py` — the other half of the dedup-identity
 *  contract for `context_ref.type`. Nothing enforces the two agree across languages; change them
 *  together. Referenced by `RECORD_NOUN` below and by `replayView`'s dispatch in
 *  `useChatWorkspace.ts`, instead of each hand-writing the string literal separately. */
export const CONTEXT_REF_TYPE = {
  WORKFLOW_SETTINGS: 'workflow_settings',
  COMPANY_WORKFLOWS: 'company_workflows',
  COMMUNICATION: 'communication',
  ENGAGEMENT: 'engagement',
} as const;

/** The `{type, id}` a client-recorded view is bound to. Mirrors `_RECORD_NOUN` in `actions.py`
 *  so a user-driven entry and the agent's equivalent hash to the same key. */
const RECORD_NOUN: Partial<Record<string, string>> = {
  properties: 'property',
  engagements: CONTEXT_REF_TYPE.ENGAGEMENT,
  tasks: 'task',
  my_todos: 'task',
  meetings: 'meeting',
  contacts: 'contact',
  notes: 'note',
  users: 'user',
  roles: 'role',
  communications: CONTEXT_REF_TYPE.COMMUNICATION,
  notifications: 'notification',
  workflow: CONTEXT_REF_TYPE.ENGAGEMENT,
};

export function contextRefFor(module: string, recordId?: string | null): RecentContextRef | undefined {
  if (!recordId) return undefined;
  return { type: RECORD_NOUN[module] ?? module, id: recordId };
}

const MODULE_NAMES: Partial<Record<string, string>> = {
  tasks: 'Tasks', meetings: 'Meetings', my_todos: 'My To-Dos',
  properties: 'Properties', contacts: 'Contacts', notes: 'Notes',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A short human heading for a view, derived from its module + filter.
 *
 *  Used for entries with no agent-authored title — a manual filter click, a `+` create — and as
 *  the menu's fallback. Single definition shared by the menu and the cards engine; it used to
 *  live only in `useChatWorkspace`, so user-driven views had no label at all. */
export function viewLabel(module: string, params?: Record<string, unknown> | null): string {
  const head = MODULE_NAMES[module] ?? (module.charAt(0).toUpperCase() + module.slice(1).replace(/_/g, ' '));
  const f = normalizeViewParams(params);

  const sortBy = (f.sort_by as string) ?? '';
  const sortDir = (f.sort_dir as number) ?? -1;
  let sortLabel = '';
  if (sortBy === 'updated_on') sortLabel = sortDir >= 0 ? 'Oldest Updated' : 'Recently Updated';
  else if (sortBy === 'created_on') sortLabel = sortDir >= 0 ? 'Oldest First' : 'Recently Created';

  // Remaining human-readable filters (skip sort keys, ids and raw UUIDs — they read as noise).
  const parts = Object.entries(f)
    .filter(([k, v]) =>
      k !== 'sort_by' && k !== 'sort_dir' &&
      v !== false &&
      !k.endsWith('_id') &&
      !UUID_RE.test(String(v)),
    )
    .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' ')}: ${v}`);

  const qualifier = [sortLabel, ...parts].filter(Boolean).join(', ');
  return qualifier ? `${head} · ${qualifier}` : head;
}

/** The heading to show for an entry the agent never titled. */
export function viewFallbackLabel(entry: Pick<RecentEntry, 'cardType' | 'viewParams'>): string {
  return viewLabel(entry.cardType, entry.viewParams);
}
